import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { HourglassMedium, ArrowsClockwise, Prohibit } from '@phosphor-icons/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { approvalsApi } from '@/lib/api'
import { cn, getBadgeClasses, badgeVariantBorderStyles } from '@/lib/utils'
import { cardChromeClass, iconChipClass, dangerBannerClass, bannerTextStyle } from '@/lib/styleKit'
import type { MyApprovalRequest } from '@/types/approvals'

const STATUS_LABELS: Record<MyApprovalRequest['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
}

const chipStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }

/** Where the row's name points: the builder for a workflow, the detail page for a campaign. */
export function requestTargetPath(req: Pick<MyApprovalRequest, 'type' | 'target_id'>): string {
  return req.type === 'workflow' ? `/workflows/builder/${req.target_id}` : `/campaigns/${req.target_id}`
}

/** The date worth showing: the decision when there is one, otherwise the submission. */
export function requestDisplayDate(req: Pick<MyApprovalRequest, 'decided_at' | 'requested_at'>): string | null {
  const raw = req.decided_at || req.requested_at
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString()
}

/**
 * Settings > My Approval Requests (spec 5.8, brief C5): a Representative's
 * own list of everything they sent for review, with the approver, the
 * decision and the rejection reason. Read-only; the fix-and-resubmit happens
 * on the workflow or campaign itself, which each row links to.
 */
const MyApprovalRequestsSection: React.FC = () => {
  const [rows, setRows] = useState<MyApprovalRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await approvalsApi.myRequests()
      setRows(res.data)
    } catch {
      // The API client's interceptor already toasted; keep whatever we had.
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          My Approval Requests
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          Workflows and campaigns you sent for review, and what your approver decided.
        </p>
      </div>

      <Card className={cardChromeClass}>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className={iconChipClass}>
                <HourglassMedium className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
              </div>
              <div>
                <CardTitle>Your requests</CardTitle>
                <CardDescription>Newest first. A rejected item can be fixed and sent again.</CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              loading={loading}
              onClick={() => load()}
              icon={<ArrowsClockwise className="w-3.5 h-3.5" weight="regular" />}
              aria-label="Refresh my approval requests"
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
            >
              {loading && !loaded ? 'Loading…' : 'You have not sent anything for approval yet.'}
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((req) => {
                const date = requestDisplayDate(req)
                return (
                  <div
                    key={req.id}
                    data-testid={`approval-request-${req.id}`}
                    className="px-3 py-2.5 rounded-md bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] space-y-2"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex items-center gap-2 flex-wrap">
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded-full uppercase border',
                            req.type === 'workflow' ? badgeVariantBorderStyles.info : badgeVariantBorderStyles.default,
                          )}
                          style={chipStyle}
                        >
                          {req.type === 'workflow' ? 'Workflow' : 'Campaign'}
                        </span>
                        <Link
                          to={requestTargetPath(req)}
                          className="text-foreground dark:text-[#f7f8f8] truncate hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc] transition-colors duration-150"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {req.target_name}
                        </Link>
                        <span
                          className={cn('inline-flex items-center px-2 py-0.5 rounded-full uppercase', getBadgeClasses(req.status))}
                          style={chipStyle}
                        >
                          {STATUS_LABELS[req.status]}
                        </span>
                      </div>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] flex items-center gap-2 flex-wrap"
                        style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                      >
                        <span>Sent to {req.approver_name || 'an approver'}</span>
                        {date && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{date}</span>
                          </>
                        )}
                      </p>
                    </div>
                    {req.status === 'rejected' && req.reason && (
                      <div className={dangerBannerClass} role="note">
                        <Prohibit className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" weight="fill" />
                        <div style={bannerTextStyle}>
                          <span style={{ fontWeight: 590 }}>Reason:</span> {req.reason}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default MyApprovalRequestsSection
